import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ToneBadge } from "@/features/oversight/oversight-ui";
import { BROADCAST_STATS, BROADCAST_ROWS } from "@/features/oversight/oversight-data";
import type { SystemStatusItem } from "../dashboard-selectors";

export function SystemStatus({ items }: { items: SystemStatusItem[] }) {
  const confirmed = Number(BROADCAST_STATS.find((s) => s.label === "Confirmed")?.value ?? 0);
  const inFlight = Number(BROADCAST_STATS.find((s) => s.label === "In-Flight")?.value ?? 0);
  const queued = Number(BROADCAST_STATS.find((s) => s.label === "Queued")?.value ?? 0);
  const stuck = Number(BROADCAST_STATS.find((s) => s.label === "Stuck")?.value ?? 0);
  const failed = Number(BROADCAST_STATS.find((s) => s.label === "Failed")?.value ?? 0);
  const latest = BROADCAST_ROWS.slice(0, 5);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">System Status</h2>
        <Link to="/monitoring" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col justify-between gap-4">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between gap-3 rounded-lg bg-bg px-3 py-2.5"
          >
            <span className="truncate text-sm font-medium text-ink">{item.label}</span>
            <ToneBadge tone={item.tone} dot={false}>
              {item.statusLabel}
            </ToneBadge>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Broadcast state</div>
        <div className="flex items-center gap-3 rounded-lg bg-bg px-3 py-2.5 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-block size-2 rounded-full bg-success" />
            <span className="text-muted">Confirmed</span>
            <span className="font-semibold text-ink">{confirmed}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block size-2 rounded-full bg-info" />
            <span className="text-muted">In-Flight</span>
            <span className="font-semibold text-ink">{inFlight}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block size-2 rounded-full bg-pending" />
            <span className="text-muted">Queued</span>
            <span className="font-semibold text-ink">{queued}</span>
          </div>
          {(stuck > 0 || failed > 0) && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full bg-danger" />
              <span className="text-muted">Issues</span>
              <span className="font-semibold text-ink">{stuck + failed}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Latest broadcasts</div>
        <div className="space-y-1">
          {latest.map((row) => (
            <Link
              key={row.txId}
              to={`/transfers/${row.txId}`}
              className="flex items-center justify-between gap-2 rounded-lg bg-bg px-3 py-2 text-xs transition-colors hover:bg-surface"
            >
              <span className="truncate font-mono text-muted">{row.txId}</span>
              <ToneBadge tone={row.statusTone} dot={false}>
                {row.statusLabel}
              </ToneBadge>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
